import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';

import i18n from '@nextroll/ar-i18n';
import { Link } from '@nextroll/ar-container-app';

import { useRoflStore } from 'store/roflStore';
import { WORKFLOW_BASE } from 'types/rofl';
import useRequest from 'hooks/useRequest';
import useAbortController from 'hooks/useAbortController';
import { getOrganizationUserEmails } from 'api/adrollApi';
import { getAdvertisableUserEmails } from 'api/alignmentApi';
import { getWorkflowActionPropertyValues } from 'api/workflowActionsApi';
import { isFeatureFlagEnabled, RW_WORKFLOWS_SEND_CONTACTS_IN_EMAIL } from 'util/featureFlags';

import { Chips } from 'components/basic/Chips/Chips';


export const EMAIL_NODE_ACTION_EID = 'NVB4QOSPFBBPBEL4LV8RFL';

export const ActionNodeEmailPanelHeader = () => (
    <div data-testid='node-header-section' className='workflow-node-panel-header-container'>
        <i className='fas fa-envelope' style={{ margin: '4px 12px 4px 0' }} />
        <h4>
            {i18n.gettext('Email')}
            <div className='workflow-node-panel-header-container-help-section'>
                <div className='workflow-node-panel-header-container-help-section-text'>
                    {i18n.gettext('Send an email when contacts meet trigger criteria')}
                </div>
                <Link
                    to='https://help.rollworks.com/hc/articles/14220880203405'
                    openNewWindow
                    rel='noopener noreferrer'
                >
                    {i18n.gettext('AdRoll Help Center: Notification via Workflows')}
                    &nbsp;
                    <i className='ar-icon ar-icon-fw fa-external-link-alt' aria-hidden />
                </Link>
            </div>
        </h4>
    </div>
);

/**
 * following 2 are reusable components for rendering email dropdown options
 */
const ActionNodeEmailPanelSelectSubHeader = React.forwardRef((props: {
    label: string;
    title: string;
    expanded: boolean;
    onSetExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    onChangeAllEmails: (selected: boolean) => void;
}, ref: React.ForwardedRef<HTMLInputElement>) => (
    <li key={`workflow-action-email-checkbox-${props.label}`} role='option' className='ar-select-option'>
        <div className='ar-popper-wrapper ar-select-option-label workflow-node-panel-action-email-select-option-head'>
            <i
                className={`fas fa-angle-down fa-lg${props.expanded ? ' expanded' : ''}`}
                aria-hidden='true'
                onClick={() => props.onSetExpanded(isExpanded => !isExpanded)}
            />
            <input
                ref={ref}
                id={`workflow-action-email-checkbox-${props.label}`}
                type='checkbox'
                onChange={e => props.onChangeAllEmails(e.target.checked)}
                className='ar-checkbox'
            />
            <label htmlFor={`workflow-action-email-checkbox-${props.label}`}>{props.title}</label>
        </div>
    </li>
));
const ActionNodeEmailPanelSelectOption = ({ email, emails, setEmails }: {
    email: string;
    emails: { [email: string]: boolean };
    setEmails: React.Dispatch<React.SetStateAction<{ [email: string]: boolean }>>;
}) => (
    <li role='option' className='ar-select-option'>
        <div className='ar-popper-wrapper ar-select-option-label workflow-node-panel-action-email-select-option'>
            <input
                id={`workflow-action-email-checkbox-${email}`}
                type='checkbox'
                checked={emails[email]}
                onChange={e => setEmails({ ...emails, [email]: e.target.checked })}
                className='ar-checkbox'
            />
            <label htmlFor={`workflow-action-email-checkbox-${email}`}>{email}</label>
        </div>
    </li>
);

interface IProps {
    advertisableEid?: string;
    title?: string;
    customHeader?: React.ReactNode;
    hideFooter?: boolean;
    hideSalesInsightEmails?: boolean;
    emails: string[];
    onUpdateEmails: (emails: string[]) => void;
}

export const ActionNodeEmailPanel = (props: IProps) => {
    const { advertisableEid, hideSalesInsightEmails, onUpdateEmails } = props;

    const [base] = useRoflStore(state => [state.base]);
    const [call] = useRequest();
    const getSignal = useAbortController();

    const [organizationEmails, setOrganizationEmails] = useState({} as { [email: string]: boolean });
    const [salesInsightEmails, setSalesInsightEmails] = useState({} as { [email: string]: boolean });

    const organizationEmailsRef = useRef<HTMLInputElement>(null);
    const salesInsightEmailsRef = useRef<HTMLInputElement>(null);

    const [expandOrganizationEmails, setExpandOrganizationEmails] = useState(true);
    const [expandSalesInsightEmails, setExpandSalesInsightEmails] = useState(true);

    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);

    /**
     * click-away listener for closing the menu when clicking at else-where
     */
    const containerRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !(containerRef.current as any).contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [containerRef, setOpen]);

    /**
     * fetch organization user emails based on the current workflow action
     * the email node is shared between both send-email and the contact redemption export actions
     */
    const getOrganizationEmails = useRef((eid: string, abortSignal: AbortSignal) => {
        if (base === WORKFLOW_BASE.CONTACTS) {
            return call(() => getOrganizationUserEmails(eid, abortSignal));
        }
        // stop here if feature flag is OFF to protect user information
        // TODO: remove this condition after the release of the contact redemption export action
        if (!isFeatureFlagEnabled(RW_WORKFLOWS_SEND_CONTACTS_IN_EMAIL)) {
            return [];
        }
        return getWorkflowActionPropertyValues({
            advertisableEid: eid,
            workflowActionEid: 'KMJW4GWNNFFJFM4GPO8RFL',
            propertyName: 'emails',
            call,
            signal: abortSignal,
        }).then(users => (users || []).map(({ value }) => value));
    });

    /**
     * fetch user emails via requests
     */
    const fetch = useRef(async () => {
        if (!advertisableEid) {
            return;
        }
        const abortSignal = getSignal();
        const [rawOrganizationEmails, rawSalesInsightEmails] = await Promise.all([
            // 1. get organization user emails
            getOrganizationEmails.current(advertisableEid, abortSignal),
            // 2. get sales insight user emails, if necessary
            !hideSalesInsightEmails
                ? call(() => getAdvertisableUserEmails(advertisableEid, abortSignal))
                : [], // skip fetching sales insight emails if specified
        ]) as [string[], string[]];
        if (abortSignal.aborted) {
            return;
        }
        setOrganizationEmails(Array.from(new Set(rawOrganizationEmails))
            .reduce((map, email) => ({ ...map, [email]: props.emails.includes(email) }), {}));
        !hideSalesInsightEmails && setSalesInsightEmails(Array.from(new Set(rawSalesInsightEmails))
            .reduce((map, email) => ({
                ...map,
                ...(rawOrganizationEmails.includes(email) ? {} : { [email]: props.emails.includes(email) }),
            }), {}));
        setLoading(false);
    });
    useEffect(() => {
        fetch.current();
    }, [fetch]);

    const sortedOrganizationEmails = useMemo(() => {
        const lowerCaseSearch = search.toLowerCase();
        return Object.keys(organizationEmails).filter(email => email.toLowerCase().includes(lowerCaseSearch)).sort();
    }, [organizationEmails, search]);

    const sortedSalesInsightEmails = useMemo(() => {
        const lowerCaseSearch = search.toLowerCase();
        return Object.keys(salesInsightEmails).filter(email => email.toLowerCase().includes(lowerCaseSearch)).sort();
    }, [salesInsightEmails, search]);

    const selectedOrganizationEmails = useMemo(
        () => Object.keys(organizationEmails).filter(email => organizationEmails[email]).sort(),
        [organizationEmails],
    );
    const selectedSalesInsightEmails = useMemo(
        () => Object.keys(salesInsightEmails).filter(email => salesInsightEmails[email]).sort(),
        [salesInsightEmails],
    );

    useEffect(() => {
        if (!organizationEmailsRef.current) {
            return;
        }
        if (selectedOrganizationEmails.length === 0) {
            organizationEmailsRef.current.checked = false;
            organizationEmailsRef.current.indeterminate = false;
        } else if (selectedOrganizationEmails.length === Object.keys(organizationEmails).length) {
            organizationEmailsRef.current.checked = true;
            organizationEmailsRef.current.indeterminate = false;
        } else {
            organizationEmailsRef.current.checked = false;
            organizationEmailsRef.current.indeterminate = true;
        }
    }, [selectedOrganizationEmails.length, organizationEmails]);
    useEffect(() => {
        if (!salesInsightEmailsRef.current) {
            return;
        }
        if (selectedSalesInsightEmails.length === 0) {
            salesInsightEmailsRef.current.checked = false;
            salesInsightEmailsRef.current.indeterminate = false;
        } else if (selectedSalesInsightEmails.length === Object.keys(salesInsightEmails).length) {
            salesInsightEmailsRef.current.checked = true;
            salesInsightEmailsRef.current.indeterminate = false;
        } else {
            salesInsightEmailsRef.current.checked = false;
            salesInsightEmailsRef.current.indeterminate = true;
        }
    }, [selectedSalesInsightEmails.length, salesInsightEmails]);

    // sync selected emails to parent component when necessary
    const selectedEmails = useMemo(
        () => [...selectedOrganizationEmails, ...selectedSalesInsightEmails],
        [selectedOrganizationEmails, selectedSalesInsightEmails],
    );
    useEffect(() => {
        selectedEmails.join('') !== props.emails.join('') && onUpdateEmails(selectedEmails);
    }, [selectedEmails, props.emails, onUpdateEmails]);

    /**
     * event handlers
     */

    const handleChangeAllOrganizationEmails = useCallback((selected: boolean) => {
        const emails = [...Object.keys(organizationEmails)].reduce((map, email) => ({ ...map, [email]: selected }), {});
        setOrganizationEmails(emails);
        if (organizationEmailsRef.current) {
            organizationEmailsRef.current.checked = selected;
            organizationEmailsRef.current.indeterminate = false;
        }
    }, [organizationEmails, setOrganizationEmails]);
    const handleChangeAllSalesInsightEmails = useCallback((selected: boolean) => {
        const emails = [...Object.keys(salesInsightEmails)].reduce((map, email) => ({ ...map, [email]: selected }), {});
        setSalesInsightEmails(emails);
        if (salesInsightEmailsRef.current) {
            salesInsightEmailsRef.current.checked = selected;
            salesInsightEmailsRef.current.indeterminate = false;
        }
    }, [salesInsightEmails, setSalesInsightEmails]);

    const handleDelete = useCallback((email: string) => {
        organizationEmails[email] && setOrganizationEmails({ ...organizationEmails, [email]: false });
        salesInsightEmails[email] && setSalesInsightEmails({ ...salesInsightEmails, [email]: false });
    }, [organizationEmails, setOrganizationEmails, salesInsightEmails, setSalesInsightEmails]);

    const handleClear = useCallback(() => {
        // clear email selections
        setOrganizationEmails(Object.keys(organizationEmails).reduce((map, email) => ({ ...map, [email]: false }), {}));
        setSalesInsightEmails(Object.keys(salesInsightEmails).reduce((map, email) => ({ ...map, [email]: false }), {}));
        setSearch(''); // clear search text
    }, [organizationEmails, setOrganizationEmails, salesInsightEmails, setSalesInsightEmails, setSearch]);

    /**
     * following methods are for preparing dropdown options
     */
    // header and options of organization emails
    const dropdownOrganizationEmailHeader = useMemo(() => (
        <ActionNodeEmailPanelSelectSubHeader
            ref={organizationEmailsRef}
            label='rollworks-users'
            title={i18n.gettext('AdRoll Users')}
            expanded={expandOrganizationEmails}
            onSetExpanded={setExpandOrganizationEmails}
            onChangeAllEmails={handleChangeAllOrganizationEmails}
        />
    ), [expandOrganizationEmails, setExpandOrganizationEmails, handleChangeAllOrganizationEmails]);
    const dropdownOrganizationEmails = useMemo(() => sortedOrganizationEmails.map(email => (
        <ActionNodeEmailPanelSelectOption
            key={`workflow-action-email-checkbox-${email}`}
            email={email}
            emails={organizationEmails}
            setEmails={setOrganizationEmails}
        />
    )), [organizationEmails, setOrganizationEmails, sortedOrganizationEmails]);
    // header and options of sales insight emails
    const dropdownSalesInsightEmailHeader = useMemo(() => !hideSalesInsightEmails && (
        <ActionNodeEmailPanelSelectSubHeader
            ref={salesInsightEmailsRef}
            label='insight-users'
            title={i18n.gettext('Sales Insight Users')}
            expanded={expandSalesInsightEmails}
            onSetExpanded={setExpandSalesInsightEmails}
            onChangeAllEmails={handleChangeAllSalesInsightEmails}
        />
    ), [
        expandSalesInsightEmails, setExpandSalesInsightEmails,
        hideSalesInsightEmails, handleChangeAllSalesInsightEmails,
    ]);
    const dropdownSalesInsightEmails = useMemo(() => !hideSalesInsightEmails && sortedSalesInsightEmails.map(email => (
        <ActionNodeEmailPanelSelectOption
            key={`workflow-action-email-checkbox-${email}`}
            email={email}
            emails={salesInsightEmails}
            setEmails={setSalesInsightEmails}
        />
    )), [hideSalesInsightEmails, salesInsightEmails, setSalesInsightEmails, sortedSalesInsightEmails]);
    // the actual dropdown content
    const dropdowns = useMemo(() => {
        if (hideSalesInsightEmails) {
            return <ul className='ar-select-option-list'>{dropdownOrganizationEmails}</ul>;
        }
        // both organization & sales insight emails
        return (
            <ul className='ar-select-option-list ar-select-option-list-scroll'>
                {dropdownOrganizationEmailHeader}
                {expandOrganizationEmails && dropdownOrganizationEmails}
                {dropdownSalesInsightEmailHeader}
                {expandSalesInsightEmails && dropdownSalesInsightEmails}
            </ul>
        );
    }, [
        hideSalesInsightEmails,
        dropdownOrganizationEmailHeader, expandOrganizationEmails, dropdownOrganizationEmails,
        dropdownSalesInsightEmailHeader, expandSalesInsightEmails, dropdownSalesInsightEmails,
    ]);

    return (
        <div className='workflow-node-panel-action-email'>
            <section>
                {props.customHeader ?? null}
            </section>
            <section>
                <p><strong>{props.title || i18n.gettext('Notification recipients')}</strong></p>
                <Chips labels={selectedEmails} onDelete={(_, email) => handleDelete(email)} className='mb-2' />

                <div className='workflow-node-panel-action-email-select' ref={containerRef} onFocus={() => setOpen(true)}>
                    <input
                        type='text'
                        placeholder={i18n.gettext('Select users or search')}
                        disabled={loading}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className='form-control ar-input-group-input'
                    />
                    <div className='workflow-node-panel-action-email-select-hint'>
                        {selectedEmails.length > 0 && <span>{i18n.gettext('{{ count }} selected', { count: selectedEmails.length })}</span>}
                        {loading ? <div className='ar-spinner' /> : <i className='ar-icon ar-icon-search' />}
                    </div>
                    <div className={`ar-select-menu dropdown-menu${open ? ' active' : ''}`}>
                        {dropdowns}
                    </div>
                </div>

                <button type='button' role='button' className='btn btn-link workflow-node-panel-action-email-btn-clear' onClick={handleClear}>
                    {i18n.gettext('Clear')}
                </button>
            </section>

            {!props.hideFooter && (
                <section>
                    <p><strong>{i18n.gettext('Email Content')}</strong></p>
                    <p>
                        {i18n.gettext('The email will include a list of contacts that matched the criteria. Whether you receive the same contact again will depend on the re-enrollment setting you define in the next step.')}
                    </p>
                </section>
            )}
        </div>
    );
};
